import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { SingleUploader, GalleryUploader } from "@/components/admin/Uploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Maçonnerie Générale", "Rénovation & Réhabilitation", "Béton & Ouvrages Structurels", "Murs & Clôtures", "VRD & Aménagements Extérieurs", "Finitions & Reprises"];
const CLIENTS = [["particuliers", "Particuliers"], ["professionnels", "Professionnels"], ["collectivites", "Collectivités"]];
const EMPTY = { title: "", category: CATEGORIES[0], location: "", client_type: "particuliers", year: "2025", summary: "", challenge: "", solution: "", result: "", cover_image: "", gallery: [], featured: false, published: true };

export default function RealisationsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/realisations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.slug; delete payload.created_at; delete payload.updated_at;
      if (editing) await api.put(`/admin/realisations/${editing.id}`, payload);
      else await api.post("/admin/realisations", payload);
      toast.success("Réalisation enregistrée");
      setOpen(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer « ${it.title} » ?`)) return;
    await api.delete(`/admin/realisations/${it.id}`);
    toast.success("Supprimée"); load();
  };

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-3xl font-black text-ink">Réalisations</h1><p className="text-ink/50">Gérez vos études de cas et leurs photos.</p></div>
        <button onClick={openNew} data-testid="new-realisation" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full font-bold"><Plus size={18} /> Ajouter</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.id} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`realisation-row-${it.id}`}>
            <div className="h-40 bg-muted relative">
              {it.cover_image && <img src={fileUrl(it.cover_image)} alt="" className="w-full h-full object-cover" />}
              {it.featured && <span className="absolute top-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"><Star size={12} /> À la une</span>}
              {!it.published && <span className="absolute top-2 right-2 bg-ink/80 text-white text-xs px-2 py-1 rounded-full">Brouillon</span>}
            </div>
            <div className="p-4">
              <span className="text-xs text-primary font-semibold">{it.category}</span>
              <h3 className="font-heading font-bold text-ink line-clamp-1">{it.title}</h3>
              <p className="text-xs text-ink/50">{it.location}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => openEdit(it)} className="flex-1 inline-flex items-center justify-center gap-1 text-sm border border-border rounded-lg py-2 hover:border-primary"><Pencil size={14} /> Éditer</button>
                <button onClick={() => remove(it)} className="px-3 border border-border rounded-lg text-red-600 hover:border-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier" : "Nouvelle"} réalisation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-xs font-semibold text-ink/60">Titre *</label><input data-testid="rea-title" className={field} value={form.title} onChange={set("title")} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-semibold text-ink/60">Catégorie</label><select className={field} value={form.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><label className="text-xs font-semibold text-ink/60">Type de client</label><select className={field} value={form.client_type} onChange={set("client_type")}>{CLIENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-semibold text-ink/60">Lieu</label><input className={field} value={form.location} onChange={set("location")} placeholder="Ville (CP)" /></div>
              <div><label className="text-xs font-semibold text-ink/60">Année</label><input className={field} value={form.year} onChange={set("year")} /></div>
            </div>
            <div><label className="text-xs font-semibold text-ink/60">Résumé</label><textarea rows={2} className={field} value={form.summary} onChange={set("summary")} /></div>
            <div><label className="text-xs font-semibold text-ink/60">Le défi</label><textarea rows={2} className={field} value={form.challenge} onChange={set("challenge")} /></div>
            <div><label className="text-xs font-semibold text-ink/60">Notre solution</label><textarea rows={2} className={field} value={form.solution} onChange={set("solution")} /></div>
            <div><label className="text-xs font-semibold text-ink/60">Le résultat</label><textarea rows={2} className={field} value={form.result} onChange={set("result")} /></div>
            <div><label className="text-xs font-semibold text-ink/60 block mb-1">Photo de couverture</label><SingleUploader testid="rea-cover" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} /></div>
            <div><label className="text-xs font-semibold text-ink/60 block mb-1">Galerie</label><GalleryUploader value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> À la une</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publié</label>
            </div>
            <button onClick={save} disabled={saving} data-testid="rea-save" className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-full disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} Enregistrer</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
